import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import { formatDateFromDayjsWithLocale } from '@utils/dateWithTz';
import { isNotBlank, isNullOrUndefined } from '@utils/utils';
import { FilterConfig, FilterType, TreeFilterGroup } from '../types';

/** Separator between values of a multi-value filter (kept non-bold while the values are bold). */
export const MULTI_VALUE_SEPARATOR = ' | ';

/** Renders a chip value with bold values; for multi-value strings the separating "|" stays non-bold. */
export const renderChipValue = (valueLabel: string): React.ReactNode =>
  valueLabel.split(MULTI_VALUE_SEPARATOR).map((part, i) => (
    <Fragment key={i}>
      {i > 0 && <span className="font-normal text-content-tertiary">{MULTI_VALUE_SEPARATOR}</span>}
      <span className="font-bold">{part}</span>
    </Fragment>
  ));

/**
 * Anything a filter value can hold, rendered as the chip's text. A hand-edited URL can repeat a param
 * (`?applicationId=a&applicationId=b`), which parses to an array where a single value is expected, and
 * a chip label must not take the page down over it.
 */
const toChipText = (value: unknown): string => {
  if (isNullOrUndefined(value)) return '';
  return Array.isArray(value) ? value.map(item => String(item)).join(MULTI_VALUE_SEPARATOR) : String(value);
};

/** Display label for a filter field — explicit `label`, else `placeholder`, else the first key. */
export const getFilterLabel = (conf: FilterConfig): string => conf.label ?? conf.placeholder ?? conf.value?.[0] ?? '';

/**
 * Identity of a filter within the bar — explicit `id`, else the first filter key. Two filters may
 * edit the same keys (e.g. two ownership-scoped trees over `locationIds`/`rampIds`), so the chip key,
 * the hearted-defaults entry and the "add filter" option all go through this rather than `value[0]`.
 */
export const getFilterKey = (conf: FilterConfig): string => conf.id ?? conf.value[0];

/**
 * How many leading filters the legacy desktop bar kept always visible (its "primary filters" row,
 * before the "Show filters" expander). The v2 bar treats that same prefix as the default chip set.
 */
const LEGACY_PRIMARY_FILTER_COUNT = 7;

/**
 * Filters shown by default (hearted) until the user picks their own: the legacy bar's first 7, with
 * `defaultShown` overriding the position either way. Shared by the chip bar and any sidebar that
 * edits the hearts, so both agree on what "not customised yet" shows.
 */
export const defaultHeartedKeys = (filtersConf: FilterConfig[]): string[] =>
  filtersConf.filter((conf, i) => conf.defaultShown ?? i < LEGACY_PRIMARY_FILTER_COUNT).map(conf => getFilterKey(conf));

/** Shallow value equality (treats null/undefined alike) — used to skip no-op filter applies. */
export const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Debounce before a chip edit refreshes the data (the chip itself updates immediately). */
export const FILTER_APPLY_DELAY_MS = 400;

/** Select-style filters (their own searchable panel) are handled by SelectFilterChip; the rest by FilterChip. */
export const isSelectType = (conf: FilterConfig): boolean =>
  conf.filterType === FilterType.Dropdown ||
  conf.filterType === FilterType.DropdownLazy ||
  conf.filterType === FilterType.MultiSelect ||
  conf.filterType === FilterType.MultiSelectLazy;

const readKey = (filter: any, key: string) => (filter && typeof filter === 'object' && key in filter ? filter[key] : undefined);

const asArray = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

/**
 * Parent ids implied by a child selection: a parent counts as selected once *every* one of its
 * children is. Keeping this derivation in one place means the chip, the value label and the callers
 * all agree on what "the location is selected" means.
 */
export const deriveSelectedParents = (groups: TreeFilterGroup[], selectedChildren: string[]): string[] =>
  groups.filter(g => g.children.length > 0 && g.children.every(c => selectedChildren.includes(c.value))).map(g => g.value);

/** The selected child ids that belong to this config's own tree (the key may be shared with others). */
const ownSelectedChildren = (conf: FilterConfig, filter: any): string[] => {
  const own = new Set((conf.treeOptions ?? []).flatMap(g => g.children.map(c => c.value)));
  return asArray(readKey(filter, conf.value[1])).filter(id => own.has(id));
};

/**
 * Per-parent breakdown of a tree filter's selection: every parent with at least one selected child,
 * with how many of its children are selected. Drives both the chip value (parent names) and its
 * tooltip (names + counts).
 */
export const getTreeSelectionByParent = (conf: FilterConfig, filter: any): { label: string; selected: number; total: number }[] => {
  const selected = ownSelectedChildren(conf, filter);
  return (conf.treeOptions ?? [])
    .map(g => ({ label: g.label, selected: g.children.filter(c => selected.includes(c.value)).length, total: g.children.length }))
    .filter(g => g.selected > 0);
};

/** Whether the filter currently holds a user-meaningful value. */
export const isFilterActive = (conf: FilterConfig, filter: any): boolean => {
  switch (conf.filterType) {
    case FilterType.Calendar:
      return conf.value.some(k => isNotBlank(readKey(filter, k)));
    case FilterType.Checkbox: {
      const v = readKey(filter, conf.value[0]);
      return v === true || v === 'true';
    }
    case FilterType.MultiSelect:
    case FilterType.MultiSelectLazy: {
      const v = readKey(filter, conf.value[0]);
      return Array.isArray(v) && v.length > 0;
    }
    case FilterType.TreeMultiSelect:
      // Only ids from this tree count — several trees can share one key (see TreeFilterChip).
      return ownSelectedChildren(conf, filter).length > 0;
    default: {
      const v = readKey(filter, conf.value[0]);
      return !isNullOrUndefined(v) && !(typeof v === 'string' && !isNotBlank(v));
    }
  }
};

const formatDate = (s: string): string => (s ? formatDateFromDayjsWithLocale(dayjs(s)) : '');

/**
 * Human-readable representation of a filter's current value, shown inside the chip.
 * `tr` is used only for the boolean (Checkbox) yes/no rendering.
 */
export const getFilterValueLabel = (conf: FilterConfig, filter: any, tr: (key: string, def: string) => string): string => {
  switch (conf.filterType) {
    case FilterType.InputText:
      return toChipText(readKey(filter, conf.value[0]));
    case FilterType.MultiSelect:
    case FilterType.MultiSelectLazy: {
      const arr = readKey(filter, conf.value[0]);
      if (!Array.isArray(arr) || arr.length === 0) return '';
      return arr
        .map((item: any) => {
          if (item && typeof item === 'object' && 'label' in item) return item.label;
          const opt = conf.options?.find(o => o.value === item);
          return opt?.label ?? String(item);
        })
        .join(MULTI_VALUE_SEPARATOR);
    }
    case FilterType.Dropdown:
    case FilterType.DropdownLazy: {
      const v = readKey(filter, conf.value[0]);
      if (isNullOrUndefined(v) || v === '') return '';
      const opt = conf.options?.find(o => o.value === v);
      return opt?.label ?? String(v);
    }
    case FilterType.Calendar: {
      const from = readKey(filter, conf.value[0]);
      const to = conf.value.length > 1 ? readKey(filter, conf.value[1]) : undefined;
      if (conf.value.length === 1) return from ? formatDate(from) : '';
      if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
      if (from) return formatDate(from);
      return to ? formatDate(to) : '';
    }
    case FilterType.Checkbox: {
      const v = readKey(filter, conf.value[0]);
      return v === true || v === 'true' ? tr('global.yes', 'Yes') : tr('global.no', 'No');
    }
    case FilterType.TreeMultiSelect:
      // Every parent holding at least one selected child, whether fully or partially selected — the
      // per-parent counts are in the chip's tooltip (see getTreeSelectionByParent).
      return getTreeSelectionByParent(conf, filter)
        .map(g => g.label)
        .join(MULTI_VALUE_SEPARATOR);
    default:
      return '';
  }
};

/** Reset a single filter to its empty state (clears every key the filter owns). */
export const clearFilter = (conf: FilterConfig, applyFilterValue: (key: string, value: any) => void): void => {
  switch (conf.filterType) {
    case FilterType.MultiSelect:
    case FilterType.MultiSelectLazy:
      applyFilterValue(conf.value[0], []);
      break;
    case FilterType.Checkbox:
      applyFilterValue(conf.value[0], false);
      break;
    case FilterType.InputText:
      applyFilterValue(conf.value[0], '');
      break;
    case FilterType.Calendar:
      conf.value.forEach(key => applyFilterValue(key, null));
      break;
    case FilterType.TreeMultiSelect:
      conf.value.forEach(key => applyFilterValue(key, []));
      break;
    default:
      applyFilterValue(conf.value[0], null);
  }
};
